import { useCallback, useEffect, useState } from "react";
import { Building2, Pencil, Save, Scale, ShieldAlert, Swords, Trash2, X } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";


const emptyForm = {
  first_name: "",
  last_name: "",
  gender: "male",
  weight: "",
  age: "",
  club: "",
  skill_level: "b",
  status: "registered",
};

export default function FighterPageV2() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [fighter, setFighter] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const canManage = ["super_admin", "event_admin"].includes(user?.role);

  const load = useCallback(async () => {
    const response = await api.get(`/fighters/${id}`);
    setFighter(response.data);
    setForm({
      first_name: response.data.first_name,
      last_name: response.data.last_name,
      gender: response.data.gender,
      weight: response.data.weight,
      age: response.data.age,
      club: response.data.club,
      skill_level: response.data.skill_level,
      status: response.data.status,
    });
  }, [id]);

  useEffect(() => {
    load().catch((requestError) => setError(apiError(requestError)));
  }, [load]);

  const save = async (event) => {
    event.preventDefault();
    setError("");
    try {
      await api.put(`/fighters/${id}`, { ...form, weight: Number(form.weight), age: Number(form.age) });
      await load();
      setEditing(false);
      setNotice("Kämpferprofil wurde aktualisiert.");
    } catch (requestError) {
      setError(apiError(requestError));
    }
  };

  const remove = async () => {
    if (!window.confirm("Kämpfer entfernen? Bereits eingeteilte Kämpfer werden sicher als gestrichen markiert.")) return;
    setError("");
    try {
      const response = await api.delete(`/fighters/${id}`);
      if (response.data.action === "deleted") {
        navigate(`/events/${fighter.event_id}`);
        return;
      }
      await load();
      setNotice(response.data.message);
    } catch (requestError) {
      setError(apiError(requestError));
    }
  };

  if (error && !fighter) return <div className="content-frame"><div className="form-error" data-testid="fighter-detail-error">{error}</div></div>;
  if (!fighter) return <div className="content-frame loading" data-testid="fighter-detail-loading">Kämpferprofil wird geladen …</div>;

  return <div className="content-frame narrow" data-testid="fighter-detail-page"><Link to={`/events/${fighter.event_id}`} className="back-link" data-testid="back-to-event-from-fighter-link">← ZUM EVENT</Link><section className="fighter-hero"><div className="fighter-monogram">{fighter.first_name[0]}{fighter.last_name[0]}</div><div><p className="eyebrow">KÄMPFERPROFIL</p><h1>{fighter.first_name} {fighter.last_name}</h1><p className="muted">{fighter.gender === "male" ? "Männlich" : "Weiblich"} · {fighter.age} Jahre · Klasse {fighter.skill_level.toUpperCase()}</p></div>{canManage && <div className="fighter-actions"><button className="icon-button" onClick={() => setEditing(!editing)} title="Kämpfer bearbeiten" data-testid="edit-fighter-button"><Pencil size={18} /></button><button className="icon-button danger-icon" onClick={remove} title="Kämpfer entfernen" data-testid="delete-fighter-button"><Trash2 size={18} /></button></div>}</section>{notice && <div className="success-notice" data-testid="fighter-action-notice"><ShieldAlert size={18} />{notice}</div>}{error && <div className="form-error" data-testid="fighter-action-error">{error}</div>}{editing && <form className="create-event-form fighter-edit-form" onSubmit={save} data-testid="edit-fighter-form"><div className="form-actions"><b>KÄMPFER BEARBEITEN</b><button type="button" className="icon-button" onClick={() => setEditing(false)} data-testid="cancel-edit-fighter-button"><X size={18} /></button></div><div className="form-grid"><Field label="Vorname" value={form.first_name} onChange={(value) => setForm({ ...form, first_name: value })} testId="edit-fighter-first-name-input" /><Field label="Nachname" value={form.last_name} onChange={(value) => setForm({ ...form, last_name: value })} testId="edit-fighter-last-name-input" /><label>Geschlecht<select value={form.gender} onChange={(event) => setForm({ ...form, gender: event.target.value })} data-testid="edit-fighter-gender-select"><option value="male">Männlich</option><option value="female">Weiblich</option></select></label><Field label="Gewicht (kg)" type="number" value={form.weight} onChange={(value) => setForm({ ...form, weight: value })} testId="edit-fighter-weight-input" /><Field label="Alter" type="number" value={form.age} onChange={(value) => setForm({ ...form, age: value })} testId="edit-fighter-age-input" /><Field label="Verein" value={form.club} onChange={(value) => setForm({ ...form, club: value })} testId="edit-fighter-club-input" /><label>Klasse<select value={form.skill_level} onChange={(event) => setForm({ ...form, skill_level: event.target.value })} data-testid="edit-fighter-skill-select"><option value="a">A</option><option value="b">B</option><option value="c">C</option></select></label></div><button className="button primary" data-testid="save-fighter-changes-button"><Save size={18} /> ÄNDERUNGEN SPEICHERN</button></form>}<section className="stat-grid"><div data-testid="fighter-weight-info"><Scale size={19} /><span>GEWICHT<b>{fighter.weight} KG</b></span></div><div data-testid="fighter-club-info"><Building2 size={19} /><span>VEREIN<b>{fighter.club}</b></span></div><div data-testid="fighter-class-info"><Swords size={19} /><span>KLASSE<b>{fighter.weight_class}</b></span></div></section><section className="fighter-history"><p className="eyebrow">KAMPFHISTORIE</p><h2>ANSTEHENDE & VERGANGENE KÄMPFE</h2>{fighter.matches?.length ? fighter.matches.map((match) => <Link key={match.id} to={`/match/${match.id}`} className="fight-history-row" data-testid={`fighter-match-${match.id}`}><span>Runde {match.round}</span><b>{match.status === "completed" ? "BEENDET" : "AUSSTEHEND"}</b></Link>) : <div className="empty-state" data-testid="fighter-matches-empty-state"><p>Für diesen Kämpfer sind noch keine Kämpfe angesetzt.</p></div>}</section></div>;
}

function Field({ label, type = "text", value, onChange, testId }) {
  return <label>{label}<input type={type} value={value} onChange={(event) => onChange(event.target.value)} required data-testid={testId} /></label>;
}
